import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { when } from "@/lib/rides";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PushToggle } from "@/components/PushToggle";

export function NotificationBell() {
  const qc = useQueryClient();
  const seen = useRef(null);
  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.get("/notifications").then((r) => r.data),
    refetchInterval: 6000,
  });

  useEffect(() => {
    if (!data) return;
    const ids = new Set(data.items.map((n) => n.id));
    if (seen.current) {
      data.items.filter((n) => !n.read && !seen.current.has(n.id)).forEach((n) => toast(n.title, { description: n.body }));
    }
    seen.current = ids;
  }, [data]);

  const markRead = async (open) => {
    if (!open || !data?.unread) return;
    await api.post("/notifications/read-all");
    qc.invalidateQueries({ queryKey: ["notifications"] });
  };

  return (
    <Popover onOpenChange={markRead}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell-button" className="relative grid h-10 w-10 place-items-center rounded-full border border-white/10 bg-black/60 text-white transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-[0_0_16px_hsl(var(--neon-cyan)/0.5)]">
          <Bell className="h-5 w-5" />
          {data?.unread > 0 && (
            <span data-testid="notifications-unread-count" className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-neon-pink px-1 text-[10px] font-bold text-white shadow-[0_0_10px_hsl(var(--neon-pink)/0.8)]">
              {data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-panel">
        <div className="border-b px-4 py-3 font-display font-semibold">Notifications</div>
        <div className="border-b"><PushToggle /></div>
        <div className="max-h-80 overflow-y-auto">
          {!data?.items?.length && <p className="px-4 py-6 text-sm text-muted-foreground">Nothing yet.</p>}
          {data?.items?.map((n) => (
            <div key={n.id} className="border-b px-4 py-3 last:border-0" data-testid="notification-item">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold">{n.title}</p>
                {!n.read && <span className="h-2 w-2 rounded-full bg-neon-pink" />}
              </div>
              <p className="mt-0.5 text-sm text-muted-foreground">{n.body}</p>
              <p className="mt-1 text-xs text-muted-foreground">{when(n.created_at)}</p>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
